/*! FeedbackPlus v1.6.0 | (c) ColonelParrot and other contributors | MIT License */
// This is a library that we're using that doesn't play nice with webpack so it could
// not be installed using yarn/npm. It has been heavily reduced to be more lightweight,
// rewritten to be TS friendly, and only includes necessary functionality.

interface CaptureResult {
	bitmap: ImageBitmap;
	height: number;
	width: number;
}

class FeedbackPlus {
	/**
	 * Checks whether the base APIs needed are supported
	 */
	static isSupported(): boolean {
		// eslint-disable-next-line compat/compat
		return !!navigator?.mediaDevices?.getDisplayMedia && !!window.HTMLCanvasElement;
	}

	/**
	 * Captures a screenshot of the page. Does not fallback to `html2canvas` with this implementation
	 * @param {number} timeout - Default `500`ms. Timeout between call & capture. Can be used to wait for animations to finish before capture
	 * @returns {Promise<CaptureResult>} promise - A promise that, when resolved, provides an ImageBitmap
	 */
	async capture(timeout: number = 500): Promise<CaptureResult> {
		if (FeedbackPlus.isSupported()) {
			return new Promise((resolve, reject) => {
				// eslint-disable-next-line compat/compat
				navigator.mediaDevices
					.getDisplayMedia({ video: true, preferCurrentTab: true } as any)
					.then((stream) => {
						const video = document.createElement('video');
						setTimeout(() => {
							video.srcObject = stream;
							video.onloadedmetadata = () => {
								video.play();
								video.pause();
								const canvas = document.createElement('canvas');
								canvas.width = video.videoWidth;
								canvas.height = video.videoHeight;
								const ctx = canvas.getContext('2d');
								if (ctx) {
									// This check ensures that ctx is not null
									ctx.drawImage(video, 0, 0);
									stream.getVideoTracks().forEach((track) => track.stop());
									FeedbackPlus.canvasToBitmap(canvas).then((bitmap) => resolve(bitmap));
								} else {
									reject(new Error('Unable to get canvas context'));
								}
							};
						}, timeout);
					})
					.catch((e) => reject(e));
			});
		} else {
			return Promise.reject(new Error('FeedbackPlus is not supported on this platform.'));
		}
	}

	/**
	 * Converts a canvas to an ImageBitmap. Can be called on the canvas returned by {@link showEditDialog()} to redraw on canvas
	 * @param {HTMLCanvasElement} canvas - The canvas
	 * @returns {Promise} promise - A promise which, when resolved, returns the ImageBitmap
	 */
	static canvasToBitmap(canvas: HTMLCanvasElement): Promise<CaptureResult> {
		return new Promise((resolve, reject) => {
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Unable to get canvas context'));
				return;
			}
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			createImageBitmap(imageData)
				.then((bitmap) => {
					resolve({
						bitmap,
						width: bitmap.width,
						height: bitmap.height,
					});
				})
				.catch(reject);
		});
	}
}

export default FeedbackPlus;
